import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Badge, HStack, Input, Select, Text, VStack, Button, Box, Collapse,
  IconButton,
} from '@chakra-ui/react';
import { DownloadIcon, ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import auditLogService from '../services/auditLogService';

const ACTION_COLORS = { create: 'green', update: 'blue', delete: 'red', login: 'purple', logout: 'gray' };
const PAGE_SIZE = 20;

const AuditLogs = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ module: '', action: '', start_date: '', end_date: '', user: '' });
  const [expandedId, setExpandedId] = useState(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (filters.module) params.module = filters.module;
      if (filters.action) params.action = filters.action;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const data = await auditLogService.getAll(params);
      if (data?.results) {
        setLogs(data.results);
        setTotalCount(data.count || 0);
      } else {
        const list = Array.isArray(data) ? data : [];
        setLogs(list);
        setTotalCount(list.length);
      }
    } catch { setError(t('auditLogs.errors.loadFailed')); }
    finally { setLoading(false); }
  }, [filters.module, filters.action, filters.start_date, filters.end_date, page, t]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (f) => (e) => {
    setFilters({ ...filters, [f]: e.target.value });
    setPage(1);
  };

  const uniqueUsers = useMemo(() => {
    const map = new Map();
    logs.forEach(l => {
      const name = l.user_full_name || l.user_name;
      if (name && !map.has(name)) map.set(name, name);
    });
    return [...map.values()].sort();
  }, [logs]);

  const filteredLogs = useMemo(() => {
    if (!filters.user) return logs;
    return logs.filter(l => (l.user_full_name || l.user_name) === filters.user);
  }, [logs, filters.user]);

  const exportCSV = () => {
    const headers = [
      t('auditLogs.csv.timestamp'), t('auditLogs.csv.user'), t('auditLogs.csv.action'), t('auditLogs.csv.module'),
      t('auditLogs.csv.resourceId'), t('auditLogs.csv.description'), t('auditLogs.csv.ipAddress'), t('auditLogs.csv.changes'),
    ];
    const rows = filteredLogs.map(l => [
      new Date(l.timestamp).toLocaleString(),
      l.user_full_name || l.user_name || '',
      l.action_display || l.action || '',
      l.module || '',
      l.resource_id || '',
      (l.description || '').replace(/"/g, '""'),
      l.ip_address || '',
      l.changes ? JSON.stringify(l.changes).replace(/"/g, '""') : '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);

  const renderChanges = (log) => {
    if (!log.changes && !log.extra_data) return <Text fontSize="xs" color="gray.400">{t('auditLogs.details.noDetails')}</Text>;
    const data = log.changes || log.extra_data;
    if (typeof data === 'string') return <Text fontSize="xs" whiteSpace="pre-wrap">{data}</Text>;
    return (
      <Box fontSize="xs" bg="gray.50" p={3} borderRadius="md" maxH="200px" overflowY="auto">
        {Object.entries(data).map(([key, val]) => (
          <HStack key={key} spacing={2} mb={1} align="start">
            <Text fontWeight="600" minW="120px" color="gray.600">{key}:</Text>
            <Text color="gray.700" whiteSpace="pre-wrap">{typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val)}</Text>
          </HStack>
        ))}
      </Box>
    );
  };

  return (
    <PageShell title={t('auditLogs.title')} subtitle={t('auditLogs.subtitle', { count: totalCount })}
      breadcrumbs={[{ label: t('auditLogs.title') }]} loading={loading} error={error}
      actions={<Button leftIcon={<DownloadIcon />} size="sm" variant="outline" onClick={exportCSV} isDisabled={filteredLogs.length === 0}>{t('auditLogs.buttons.exportCsv')}</Button>}>

      <HStack mb={4} spacing={3} flexWrap="wrap">
        <Select size="sm" maxW="180px" value={filters.module} onChange={setFilter('module')} placeholder={t('auditLogs.filters.allModules')}>
          {['authentication', 'reservations', 'rooms', 'billing', 'check_in_out', 'housekeeping', 'rates'].map(m =>
            <option key={m} value={m}>{m}</option>
          )}
        </Select>
        <Select size="sm" maxW="150px" value={filters.action} onChange={setFilter('action')} placeholder={t('auditLogs.filters.allActions')}>
          {['create', 'update', 'delete', 'login', 'logout'].map(a =>
            <option key={a} value={a}>{a}</option>
          )}
        </Select>
        <Select size="sm" maxW="200px" value={filters.user} onChange={(e) => setFilters({ ...filters, user: e.target.value })} placeholder={t('auditLogs.filters.allUsers')}>
          {uniqueUsers.map(u => <option key={u} value={u}>{u}</option>)}
        </Select>
        <Input size="sm" type="date" maxW="160px" value={filters.start_date} onChange={setFilter('start_date')} placeholder={t('auditLogs.filters.startDate')} />
        <Input size="sm" type="date" maxW="160px" value={filters.end_date} onChange={setFilter('end_date')} placeholder={t('auditLogs.filters.endDate')} />
      </HStack>

      <Card shadow="sm" borderWidth="1px">
        <CardBody p={0}>
          <TableContainer>
            <Table size="sm">
              <Thead><Tr><Th w="30px"></Th><Th>{t('auditLogs.columns.timestamp')}</Th><Th>{t('auditLogs.columns.user')}</Th><Th>{t('auditLogs.columns.action')}</Th><Th>{t('auditLogs.columns.module')}</Th><Th>{t('auditLogs.columns.resource')}</Th><Th>{t('auditLogs.columns.description')}</Th><Th>{t('auditLogs.columns.ip')}</Th></Tr></Thead>
              <Tbody>
                {filteredLogs.map(log => (
                  <React.Fragment key={log.id}>
                    <Tr _hover={{ bg: 'gray.50' }} cursor="pointer" onClick={() => toggleExpand(log.id)}>
                      <Td px={1}>
                        <IconButton size="xs" variant="ghost" icon={expandedId === log.id ? <ChevronUpIcon /> : <ChevronDownIcon />} aria-label={t('auditLogs.aria.expand')} />
                      </Td>
                      <Td fontSize="xs" color="gray.500" whiteSpace="nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="500">{log.user_full_name || log.user_name}</Text>
                        </VStack>
                      </Td>
                      <Td><Badge colorScheme={ACTION_COLORS[log.action] || 'gray'} fontSize="xs">{log.action_display || log.action}</Badge></Td>
                      <Td><Badge variant="outline" fontSize="xs">{log.module}</Badge></Td>
                      <Td fontSize="xs" color="gray.500">{log.resource_id || '-'}</Td>
                      <Td fontSize="sm" maxW="300px" isTruncated>{log.description}</Td>
                      <Td fontSize="xs" color="gray.400">{log.ip_address}</Td>
                    </Tr>
                    <Tr>
                      <Td colSpan={8} p={0} borderBottom={expandedId === log.id ? '1px solid' : 'none'} borderColor="gray.100">
                        <Collapse in={expandedId === log.id} animateOpacity>
                          <Box p={4} bg="gray.25">
                            {renderChanges(log)}
                          </Box>
                        </Collapse>
                      </Td>
                    </Tr>
                  </React.Fragment>
                ))}
                {filteredLogs.length === 0 && <Tr><Td colSpan={8} textAlign="center" py={8} color="gray.400">{t('auditLogs.emptyState.noLogs')}</Td></Tr>}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack justify="space-between" mt={4}>
          <Text fontSize="sm" color="gray.500">
            {t('auditLogs.pagination.showing', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, totalCount), total: totalCount })}
          </Text>
          <HStack spacing={2}>
            <IconButton size="sm" icon={<ChevronLeftIcon />} variant="outline"
              isDisabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label={t('auditLogs.pagination.previous')} />
            <Text fontSize="sm" fontWeight="500">{t('auditLogs.pagination.pageOf', { page, totalPages })}</Text>
            <IconButton size="sm" icon={<ChevronRightIcon />} variant="outline"
              isDisabled={page >= totalPages} onClick={() => setPage(p => p + 1)} aria-label={t('auditLogs.pagination.next')} />
          </HStack>
        </HStack>
      )}
    </PageShell>
  );
};

export default AuditLogs;
